import tenantTheme from '@theme';
import { Checkbox } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Flex } from '../../common';
import { ChangeOwnershipIcon } from '../../svg';
import { BulkChangeOwnerModal } from './bulk-change-owner-modal';
import { getSelectedCountLabel, isListingBulkSelectable } from './bulkSelection';

// ChangeOwnershipIcon paints its paths in the primary colour; flip them to white on the primary button.
const ButtonIcon = styled.span`
  display: inline-flex;
  svg path {
    fill: #fff;
  }
`;

/**
 * "Select all on this page" strip for My Listings bulk Change Owner. One row whose height never changes:
 * once something is selected it tints and shows the count, Clear selection and Change Owner.
 */
export const BulkSelectionBar = ({ bulkSelection }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const { selectedIds = [], setSelectedIds, listings = [] } = bulkSelection || {};

  const selectableIds = useMemo(
    () => listings.filter(isListingBulkSelectable).map((listing) => listing?.id),
    [listings],
  );

  if (!bulkSelection || !selectableIds.length) return null;

  const count = selectedIds.length;
  const allSelected = selectableIds.every((id) => selectedIds.includes(id));
  const someSelected = count > 0 && !allSelected;

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        gap="8px 12px"
        wrap
        style={{
          minHeight: 48,
          // 12px start padding puts the checkbox where the 40px selection column centres the row checkboxes.
          padding: '6px 12px',
          background: count ? tenantTheme['primary-light-4'] : 'transparent',
          transition: 'background 0.15s ease',
        }}
      >
        <Flex align="center" gap="12px">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={() => setSelectedIds(allSelected ? [] : selectableIds)}
          >
            <span className="fw-600">{t('Select all on this page')}</span>
          </Checkbox>
          {count > 0 && (
            <span className="fw-700" style={{ color: tenantTheme['primary-text-color'] }}>
              {getSelectedCountLabel(t, count)}
            </span>
          )}
        </Flex>
        {count > 0 && (
          <Flex align="center" gap="8px">
            <Button type="link" onClick={() => setSelectedIds([])}>
              {t('Clear selection')}
            </Button>
            <Button type="primary" onClick={() => setModalOpen(true)}>
              <Flex align="center" gap="8px" inline>
                <ButtonIcon>
                  <ChangeOwnershipIcon size={14} />
                </ButtonIcon>
                {t('Change Owner')}
              </Flex>
            </Button>
          </Flex>
        )}
      </Flex>
      {modalOpen && <BulkChangeOwnerModal bulkSelection={bulkSelection} onClose={() => setModalOpen(false)} />}
    </>
  );
};
